import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import axios from 'axios'
import {UserModel} from './_models'
import {Privilege} from '../../apps/privilege/privileges-list/core/_models'
import {getPrivileges} from '../../apps/privilege/privileges-list/core/_requests'
import {getUserByToken, refreshToken} from './_requests'
import {getSchoolById} from '../../apps/school/schools-list/core/_requests'
import {SchoolModel} from '../../apps/school/schools-list/core/_models'
import {getCurrentUser, getPrivilege, getSchool, setPrivilege, setupAxios} from './AuthHelpers'
import {getSchool as school_api} from './_requests'

export const storageKey = {
  token: 'token',
  refresh_token: 'refresh_token',
}

type AuthContextProps = {
  token: string | null
  refresh_token: string | null
  currentUser?: UserModel
  school?: SchoolModel
  privilege?: Privilege[]
  login: (token: string, refresh_token: string) => void
  logout: () => void
  refresh: () => void
}
const initialState = {
  token: localStorage.getItem(storageKey.token),
  refresh_token: localStorage.getItem(storageKey.refresh_token),
  currentUser: getCurrentUser(),
  school: getSchool(),
  privilege: getPrivilege(),
  login: (token: string, refresh_token: string) => {},
  logout: () => {},
  refresh: () => {},
}
const AuthContext = createContext<AuthContextProps>(initialState)

const AuthProvider = ({children}: {children: ReactNode}) => {
  const [state, setState] = useState(initialState)
  const login = (token: string, refresh_token: string) => {
    setState({...state, token, refresh_token})
    localStorage.setItem(storageKey.token, token)
    localStorage.setItem(storageKey.refresh_token, refresh_token)
  }
  const logout = useCallback(() => {
    setState({...initialState, token: null, refresh_token: null})
    localStorage.removeItem(storageKey.token)
  }, [])
  const refresh = useCallback(async () => {
    const refresh_token = localStorage.getItem(storageKey.refresh_token)
    if (refresh_token) {
      try {
        const auth = await refreshToken(refresh_token)
        if (auth) login(auth.auth_token, auth.refresh_token)
        else logout()
      } catch (error) {
        console.error('Error refreshing token', error)
        logout()
      }
    }
    // eslint-disable-next-line
  }, [])
  useEffect(() => {
    setupAxios(axios, logout, refresh)
    // eslint-disable-next-line
  }, [])
  const didRequest = useRef(false)
  useEffect(() => {
    const requestUser = async () => {
      try {
        const token = localStorage.getItem(storageKey.token)
        if (!token) {
          logout()
          const school = await school_api()
          if (school)
            setState((prev) => {
              return {...prev, school}
            })
          return
        }
        if (!didRequest.current) {
          const currentUser = await getUserByToken()
          if (currentUser)
            setState((prev) => {
              return {...prev, currentUser}
            })
          const privilege = await getPrivileges()
          if (privilege) {
            setState((prev) => {
              return {...prev, privilege: privilege.entities}
            })
            setPrivilege(privilege.entities || [])
          }
          const school = await getSchoolById(currentUser?.school_id)
          if (school)
            setState((prev) => {
              return {...prev, school}
            })
        }
      } catch (error) {
        if (!didRequest.current) logout()
      }
      return () => (didRequest.current = true)
    }
    requestUser()
  }, [state.token, logout])
  return (
    <AuthContext.Provider value={{...state, login, logout, refresh}}>
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => {
  return useContext(AuthContext)
}

export {AuthProvider, useAuth}
